<template>
    <BaseCard v-if="prompt && !dismissed" as="section" padding="sm" radius="lgr" class="prompt" :aria-labelledby="messageLines.map(messageId).join(' ')">
        <!-- Mounted with the card and filled after a beat: a region created holding its text is not announced. -->
        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -- an announcement region, not a field; a label would be read before the message -->
        <output class="sr-only" aria-live="polite" aria-atomic="true" :lang="messageLines[0]?.lang">{{ announcement }}</output>
        <div class="copy">
            <p v-for="(line, index) in messageLines" :id="messageId(line)" :key="line.lang" :lang="line.lang" :class="index === 0 ? 'heading' : 'subtitle'">{{ line.text }}</p>
            <BaseNotification v-if="errorText" theme="error" hide-dismiss class="error" :text="errorText" />
        </div>
        <div class="actions">
            <BaseButton theme="primary" size="sm" :disabled="isSaving" @click="accept">
                <span v-for="line in linesFor('accept')" :key="line.lang" :lang="line.lang" class="line">{{ line.text }}</span>
            </BaseButton>
            <BaseButton theme="tertiary" size="sm" :disabled="isSaving" @click="dismiss">
                <span v-for="line in linesFor('dismiss')" :key="line.lang" :lang="line.lang" class="line">{{ line.text }}</span>
            </BaseButton>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { DismissUiLanguagePromptRequest, UiLanguagePrompt, UiLanguagePromptResponse } from "~vue/api/generated/ui-language-prompt";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { useLoadingAnnouncement } from "~vue/composables/useLoadingAnnouncement";
import { loadLocaleMessages } from "~vue/i18n";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface PromptLine {
    lang: string;
    text: string;
}

const PROFILE_ENDPOINT = "/accounts/profile/";

const emit = defineEmits<{ accepted: [] }>();

const { t, te } = useI18n();

type PromptField = "message" | "accept" | "dismiss";
type PromptCopy = { key: string; render: (language: string, options: { locale?: string }) => string };

// Each key is also spelled out inside its t() call, which is what the catalogue check reads.
const COPY: Record<PromptField, PromptCopy> = {
    message: { key: "chat.home.uiLanguagePrompt.message", render: (language, options) => t("chat.home.uiLanguagePrompt.message", { language }, options) },
    accept: { key: "chat.home.uiLanguagePrompt.accept", render: (language, options) => t("chat.home.uiLanguagePrompt.accept", { language }, options) },
    dismiss: { key: "chat.home.uiLanguagePrompt.dismiss", render: (language, options) => t("chat.home.uiLanguagePrompt.dismiss", { language }, options) },
};

const prompt = ref<UiLanguagePrompt | null>(null);
const hasTargetCatalogue = ref(false);
const dismissed = ref(false);
const isSaving = ref(false);
const failedAction = ref<"accept" | "dismiss" | null>(null);

const errorText = computed(() => {
    if (failedAction.value === "accept") {
        return t("chat.home.uiLanguagePrompt.acceptError");
    }
    if (failedAction.value === "dismiss") {
        return t("chat.home.uiLanguagePrompt.dismissError");
    }
    return null;
});

// The target language leads when its catalogue translates the string; the English follows, or stands alone.
function linesFor(field: PromptField): PromptLine[] {
    const current = prompt.value;
    if (!current) {
        return [];
    }
    const { key, render } = COPY[field];
    const english = { lang: "en", text: render(current.ui_language, {}) };
    if (!hasTargetCatalogue.value || !te(key, current.locale)) {
        return [english];
    }
    return [{ lang: current.locale, text: render(current.native_name, { locale: current.locale }) }, english];
}

const messageLines = computed(() => linesFor("message"));
const messageId = (line: PromptLine) => `ui-language-prompt-message-${line.lang}`;

// The lead line only: a region carries one language, and each line has its own.
const announcement = useLoadingAnnouncement(
    () => !!prompt.value,
    () => messageLines.value[0]?.text ?? "",
);

const analyticsData = () => ({ interface_locale: prompt.value?.locale });

const { execute: fetchPrompt, data: promptResponse } = useFetch("/v1/ui-language-prompt", { immediate: false }).get().json<UiLanguagePromptResponse>();

onMounted(async () => {
    await fetchPrompt();
    const offered = promptResponse.value?.prompt;
    if (!offered) {
        return;
    }
    // Loaded before the prompt is shown, so the banner does not grow a second line after it appears.
    hasTargetCatalogue.value = await loadLocaleMessages(offered.locale);
    prompt.value = offered;
    void logUserInteraction(AmplitudeEventNames.INTERFACE_LANGUAGE_PROMPT_VIEWED, analyticsData());
});

// Both actions keep the prompt until the server confirms, so a failure leaves the buttons there to retry.
const accept = async () => {
    if (!prompt.value || isSaving.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.INTERFACE_LANGUAGE_PROMPT_ACCEPT_CLICKED, analyticsData());
    isSaving.value = true;
    failedAction.value = null;
    const payload = { detail: "ui_language", ui_language: prompt.value.ui_language };
    const { execute, data } = useFetch(PROFILE_ENDPOINT, { immediate: false }).post(payload).json<{ success: boolean }>();
    await execute();
    isSaving.value = false;
    if (!data.value?.success) {
        failedAction.value = "accept";
        return;
    }
    dismissed.value = true;
    emit("accepted");
};

const dismiss = async () => {
    if (!prompt.value || isSaving.value) {
        return;
    }
    isSaving.value = true;
    failedAction.value = null;
    const payload: DismissUiLanguagePromptRequest = { locale: prompt.value.locale };
    const { execute, error } = useFetch("/v1/ui-language-prompt/dismissals", { immediate: false }).post(payload);
    await execute();
    isSaving.value = false;
    if (error.value) {
        failedAction.value = "dismiss";
        return;
    }
    void logUserInteraction(AmplitudeEventNames.INTERFACE_LANGUAGE_PROMPT_CLOSED, analyticsData());
    dismissed.value = true;
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .prompt {
        @apply -mx-4 flex flex-col gap-3 bg-surface-raised text-ink shadow-sm sm:mx-0 sm:flex-row sm:items-center sm:gap-4;
    }

    .copy {
        @apply flex min-w-0 grow flex-col;
    }

    .heading {
        @apply body-serif text-balance;
    }

    .subtitle {
        @apply details-big text-ink-muted;
    }

    .error {
        @apply mt-2;
    }

    .actions {
        @apply flex shrink-0 flex-wrap items-center gap-2;
    }

    .line + .line {
        @apply ml-2 border-l border-current pl-2;
    }
}
</style>
